import { Controller } from "@hotwired/stimulus";
import {
  beginSkeletonWait,
  endSkeletonWait,
} from "../skeleton_wait_support";
import { matchesTurboFrameFetch } from "../table_frame_support";

export default class extends Controller {
  static targets = ["exportButton", "status"];
  static values = {
    exportUrl: String,
    tableFrameId: {
      type: String,
      default: "educator-home-school-usage-content-usage-log-table",
    },
  };

  connect() {
    this.hasRows = false;
    this.tableBusy = false;
    this.boundFrameLoad = this.onFrameLoad.bind(this);
    document.addEventListener("turbo:frame-load", this.boundFrameLoad);
    this.syncFromTableFrame();
  }

  disconnect() {
    if (this.boundFrameLoad) {
      document.removeEventListener("turbo:frame-load", this.boundFrameLoad);
    }
    this.endWait();
  }

  tableLoading(event) {
    if (
      event?.type === "turbo:before-fetch-request" &&
      !matchesTurboFrameFetch(event, [this.tableFrameIdValue])
    ) {
      return;
    }

    this.tableBusy = true;
    this.syncExportButton();
  }

  onFrameLoad(event) {
    if (event.target.id !== this.tableFrameIdValue) return;

    this.tableBusy = false;
    this.syncFromTableFrame(event.target);
  }

  async export(event) {
    event?.preventDefault();
    if (this.exporting || this.tableBusy || !this.hasRows) return;

    this.beginWait();

    try {
      const response = await fetch(this.exportUrlValue, {
        method: "POST",
        headers: {
          Accept: "text/csv",
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: this.exportRequestBody(),
      });

      if (!response.ok) {
        this.announce("Export could not be completed. Try again.");
        return;
      }

      const blob = await response.blob();
      const filename = this.filenameFromContentDisposition(
        response.headers.get("Content-Disposition"),
      );
      this.downloadBlob(blob, filename);
    } catch {
      this.announce("Export could not be completed. Try again.");
    } finally {
      this.endWait();
    }
  }

  beginWait() {
    this.exporting = true;
    beginSkeletonWait(this.element);
    this.syncExportButton();
  }

  endWait() {
    if (!this.exporting) return;

    this.exporting = false;
    endSkeletonWait(this.element);
    this.syncExportButton();
  }

  syncFromTableFrame(frame = document.getElementById(this.tableFrameIdValue)) {
    const marker = frame?.querySelector("[data-has-exportable-rows]");
    const raw =
      marker?.dataset.hasExportableRows ??
      frame?.dataset.hasExportableRows ??
      null;
    this.hasRows = raw === "true";
    this.syncExportButton();
  }

  syncExportButton() {
    if (!this.hasExportButtonTarget) return;

    const isDisabled =
      Boolean(this.exporting) || Boolean(this.tableBusy) || !this.hasRows;
    this.exportButtonTargets.forEach((button) => {
      button.disabled = isDisabled;
      if (isDisabled) {
        button.setAttribute("aria-disabled", "true");
      } else {
        button.removeAttribute("aria-disabled");
      }
    });
  }

  exportRequestBody() {
    const form = this.filtersForm();
    if (!form) return "";

    const formData = new FormData(form);
    const params = new URLSearchParams();
    params.set("search", formData.get("search") ?? "");

    return params.toString();
  }

  filtersForm() {
    return this.element.querySelector("form");
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }

  downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  filenameFromContentDisposition(header) {
    if (!header) return "ContentUsageLog.csv";

    const filenameStar = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
    if (filenameStar) {
      try {
        return decodeURIComponent(filenameStar[1].trim().replace(/^"|"$/g, ""));
      } catch {
        return filenameStar[1].trim().replace(/^"|"$/g, "");
      }
    }

    const filename = header.match(/filename="?([^";]+)"?/i);
    if (filename) return filename[1].trim();

    return "ContentUsageLog.csv";
  }

  announce(message) {
    if (!this.hasStatusTarget) return;

    this.statusTarget.textContent = "";
    requestAnimationFrame(() => {
      this.statusTarget.textContent = message;
    });
  }
}
